import { lazy, Suspense, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { getCollectionBySlug, updateCollection, deleteCollection } from "@/services/collections";
import { getMessagesByCollection, subscribeToMessages, unsubscribeFromMessages } from "@/services/messages";
import { getContributionsByCollection } from "@/services/contributions";
import { getRsvpsByCollection } from "@/services/rsvps";
import { getTemplateImageUrl } from "@/services/templates";
import { Button } from "@/components/ui/button";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Users, Pencil, Search, Palette, ExternalLink, CalendarDays, FileText, Gift, CreditCard, Trash2, MessageSquare, ShoppingBag, BookHeart } from "lucide-react";
import OrganizerRegistryModal from "@/components/wishlist/OrganizerRegistryModal";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import ContributeWizard from "@/components/card/ContributeWizard";

import InviteModal from "@/components/card/InviteModal";
import SettingsModal from "@/components/card/SettingsModal";
import GiftGuidanceWizard from "@/components/card/GiftGuidanceWizard";
import { SignerDetail, BubbleMedia, colorForName, getInitials } from "@/components/card/SignersBubbles";
import AppHeader from "@/components/AppHeader";
import { useCurrency } from "@/hooks/useCurrency";
import { supabase } from "@/integrations/supabase/client";

import type { Tables } from "@/integrations/supabase/types";

const FabricEditor = lazy(() => import("@/components/admin/FabricEditor"));

const CardManage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromDashboard = !!(location.state as { fromDashboard?: boolean })?.fromDashboard;
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const currency = useCurrency();

  const [collection, setCollection] = useState<Tables<"collections"> | null>(null);
  const [messages, setMessages] = useState<Tables<"messages">[]>([]);
  const [contributions, setContributions] = useState<Tables<"contributions">[]>([]);
  const [templateUrl, setTemplateUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [showGiftWizard, setShowGiftWizard] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageDraft, setMessageDraft] = useState("");
  const [savingMessage, setSavingMessage] = useState(false);
  const [wizardMode, setWizardMode] = useState<"sign" | "chipIn" | null>(null);
  const [showDateModal, setShowDateModal] = useState(false);
  const [dateOption, setDateOption] = useState<"update" | "close">("update");
  const [newDeadline, setNewDeadline] = useState<Date | undefined>(undefined);
  const [savingDate, setSavingDate] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [settingsSection, setSettingsSection] = useState<"home" | "details" | "design" | "gifts" | "chipin" | "rsvp" | "danger" | null>(null);
  const [rsvps, setRsvps] = useState<any[]>([]);
  const [selectedGroupMemberId, setSelectedGroupMemberId] = useState<string | null>(null);
  const [cardPayment, setCardPayment] = useState<{ status: string; total_amount: number; currency: string } | null>(null);
  const [templatePrice, setTemplatePrice] = useState<number | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [initiatingPayment, setInitiatingPayment] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [recipientWishlists, setRecipientWishlists] = useState<Array<{ id: string; title: string; type: string; cover_url: string | null }>>([]);
  const [showRegistryModal, setShowRegistryModal] = useState(false);
  // ── Customizer ──
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [customizerJson, setCustomizerJson] = useState<Record<string, unknown> | null>(null);
  const [loadingTemplateJson, setLoadingTemplateJson] = useState(false);

  const handleOpenCustomizer = async () => {
    if (!collection?.card_template_id) {
      toast({
        title: t("manage.notCustomizable", { defaultValue: "Not customizable" }),
        description: t("manage.notCustomizableNoTemplate", {
          defaultValue: "This card doesn't have a template attached.",
        }),
      });
      return;
    }
    setLoadingTemplateJson(true);
    try {
      const existingDesign = (collection as unknown as { card_design_json?: Record<string, unknown> | null }).card_design_json;
      if (existingDesign) {
        setCustomizerJson(existingDesign);
      } else {
        const { data: tmpl, error } = await supabase
          .from("card_templates")
          .select("polotno_json")
          .eq("id", collection.card_template_id)
          .maybeSingle();
        if (error) throw error;
        if (!tmpl?.polotno_json) {
          toast({
            title: t("manage.notCustomizable", { defaultValue: "Not customizable" }),
            description: t("manage.notCustomizableDesc", {
              defaultValue: "This template does not have an editable design yet.",
            }),
          });
          return;
        }
        setCustomizerJson(tmpl.polotno_json as Record<string, unknown>);
      }
      setCustomizerOpen(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to load template";
      toast({ variant: "destructive", title: "Error", description: message });
    } finally {
      setLoadingTemplateJson(false);
    }
  };
  void loadingTemplateJson;
  void handleOpenCustomizer;

  const handleCustomizerSave = async ({
    coverUrl,
    designJson,
  }: {
    coverUrl: string;
    designJson: Record<string, unknown>;
  }) => {
    if (!collection) return;
    try {
      const { error } = await updateCollection(collection.id, {
        custom_cover_url: coverUrl,
        card_design_json: designJson as unknown as null,
      });
      if (error) throw error;
      // Re-fetch from DB to make sure local state matches what was persisted
      // (avoids the manage envelope showing the previous template image
      // when the saved cover URL didn't propagate through optimistic state).
      const { data: fresh } = await getCollectionBySlug(collection.share_slug || "");
      if (fresh) {
        setCollection(fresh as typeof collection);
        setTemplateUrl((fresh.custom_cover_url as string) || coverUrl);
      } else {
        setCollection({
          ...collection,
          custom_cover_url: coverUrl,
          card_design_json: designJson as unknown as null,
        } as typeof collection);
        setTemplateUrl(coverUrl);
      }
      setCustomizerOpen(false);
      toast({ title: t("manage.cardUpdated", { defaultValue: "Card updated" }) });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to save";
      toast({ variant: "destructive", title: "Error", description: message });
    }
  };

  useEffect(() => {
    let channel: ReturnType<typeof subscribeToMessages> | null = null;

    const fetchCard = async () => {
      try {
        const { data: col, error: colError } = await getCollectionBySlug(slug!);
        if (colError) { setFetchError(true); setLoading(false); return; }
        if (!col) { setLoading(false); return; }
        setCollection(col);

        const [templateResult, messagesResult, contributionsResult, rsvpsResult, paymentResult, tmplPriceResult] = await Promise.all([
          col.card_template_id ? getTemplateImageUrl(col.card_template_id) : null,
          getMessagesByCollection(col.id),
          getContributionsByCollection(col.id),
          getRsvpsByCollection(col.id),
          supabase
            .from("card_payments" as any)
            .select("status, total_amount, currency")
            .eq("collection_id", col.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          col.card_template_id
            ? supabase.from("card_templates").select("price").eq("id", col.card_template_id).maybeSingle()
            : null,
        ]);

        setTemplateUrl(col.custom_cover_url || templateResult?.data?.image_url || "");
        setMessages(messagesResult.data);
        if (contributionsResult.data) setContributions(contributionsResult.data);
        setRsvps(rsvpsResult.data || []);
        if (paymentResult.data) setCardPayment(paymentResult.data as any);
        setTemplatePrice(tmplPriceResult?.data?.price ?? null);

        // Fetch recipient wishlists if recipient email is set
        if (col.recipient_email) {
          const { getGiftDataByRecipientEmail } = await import("@/services/wishlists");
          const { data: giftData } = await getGiftDataByRecipientEmail(col.recipient_email);
          const lists = (giftData?.wishlists || []).filter(
            (w: any) => w.type === "wishlist" || w.type === "registry"
          );
          setRecipientWishlists(lists.map((w: any) => ({ id: w.id, title: w.title, type: w.type, cover_url: w.cover_url })));
        }

        setLoading(false);

        channel = subscribeToMessages(col.id, (newMessage) => {
          setMessages((prev) => [...prev, newMessage as Tables<"messages">]);
        });
      } catch {
        setFetchError(true);
        setLoading(false);
      }
    };
    fetchCard();

    return () => { if (channel) unsubscribeFromMessages(channel); };
  }, [slug]);

  // Re-derive templateUrl when collection design changes
  useEffect(() => {
    if (!collection) return;
    const resolve = async () => {
      if (collection.custom_cover_url) {
        setTemplateUrl(collection.custom_cover_url);
      } else if (collection.card_template_id) {
        const result = await getTemplateImageUrl(collection.card_template_id);
        setTemplateUrl(result?.data?.image_url || "");
      } else {
        setTemplateUrl("");
      }
    };
    resolve();
  }, [collection?.custom_cover_url, collection?.card_template_id]);

  // Re-fetch template price when template changes (e.g. switched to free)
  useEffect(() => {
    if (!collection?.card_template_id) { setTemplatePrice(null); return; }
    const fetchPrice = async () => {
      const { data } = await supabase
        .from("card_templates")
        .select("price")
        .eq("id", collection.card_template_id!)
        .maybeSingle();
      setTemplatePrice(data?.price ?? null);
    };
    fetchPrice();
  }, [collection?.card_template_id]);

  // Redirect non-organizers
  useEffect(() => {
    if (!authLoading && !loading && collection) {
      if (!user || user.id !== collection.organizer_id) {
        navigate(`/card/${slug}`, { replace: true });
      }
    }
  }, [user, authLoading, loading, collection, slug, navigate]);

  // Handle return from Stripe after template upgrade payment
  useEffect(() => {
    if (!collection) return;
    const params = new URLSearchParams(location.search);
    const upgradeTemplateId = params.get("upgrade_template");
    if (!upgradeTemplateId) return;

    const applyUpgrade = async () => {
      const { error } = await updateCollection(collection.id, {
        card_template_id: upgradeTemplateId,
        custom_cover_url: null,
      });
      if (error) {
        toast({ variant: "destructive", title: t("common.error"), description: error.message });
      } else {
        setCollection({ ...collection, card_template_id: upgradeTemplateId, custom_cover_url: null });
        toast({ title: t("manage.designUpgraded", { defaultValue: "Design upgraded!" }), description: t("manage.designUpgradedDesc", { defaultValue: "Your card now uses the new design." }) });
      }
      // Clean URL params
      navigate(`/card/${slug}/manage`, { replace: true });
    };
    applyUpgrade();
  }, [collection?.id]);

  const openMessageModal = () => {
    setMessageDraft(collection?.message_to_group || "");
    setShowMessageModal(true);
  };

  const handleSaveMessage = async () => {
    if (!collection) return;
    setSavingMessage(true);
    const trimmed = messageDraft.trim();
    const { error } = await updateCollection(collection.id, {
      message_to_group: trimmed || null,
    });
    if (error) {
      toast({ variant: "destructive", title: t("common.error"), description: error.message });
      setSavingMessage(false);
      return;
    }
    setCollection({ ...collection, message_to_group: trimmed || null });
    toast({ title: t("settings.messageSaved", { defaultValue: "Message saved" }) });
    setSavingMessage(false);
    setShowMessageModal(false);
  };

  const handleDelete = async () => {
    if (!collection) return;
    setDeleting(true);
    const { error } = await deleteCollection(collection.id);
    if (error) {
      toast({ variant: "destructive", title: t("common.error"), description: error.message });
      setDeleting(false);
      return;
    }
    toast({ title: t("settings.deleted"), description: t("settings.deletedDesc") });
    setShowDeleteDialog(false);
    setDeleting(false);
    navigate("/dashboard");
  };

  const handleSaveDate = async () => {
    if (!collection) return;
    setSavingDate(true);
    if (dateOption === "close") {
      const { error } = await updateCollection(collection.id, {
        status: "closed",
        deadline: new Date().toISOString(),
      });
      if (error) {
        toast({ variant: "destructive", title: t("common.error"), description: error.message });
      } else {
        setCollection({ ...collection, status: "closed", deadline: new Date().toISOString() });
        toast({ title: t("cardView.cardClosed"), description: t("cardView.cardClosedDesc") });
      }
    } else if (newDeadline) {
      const { error } = await updateCollection(collection.id, {
        deadline: newDeadline.toISOString(),
      });
      if (error) {
        toast({ variant: "destructive", title: t("common.error"), description: error.message });
      } else {
        setCollection({ ...collection, deadline: newDeadline.toISOString() });
        toast({ title: t("cardView.dateUpdated"), description: t("cardView.dateUpdatedDesc") });
      }
    }
    setSavingDate(false);
    setShowDateModal(false);
  };

  const isDraft = collection?.status === "draft";
  const isPaidTemplate = templatePrice != null && templatePrice > 0;
  const paymentPending = isDraft && isPaidTemplate && (!cardPayment || cardPayment.status !== "completed");

  const handlePublish = async () => {
    if (!collection) return;
    setPublishing(true);
    const { error } = await updateCollection(collection.id, { status: "active" });
    if (error) {
      toast({ variant: "destructive", title: t("common.error"), description: error.message });
    } else {
      setCollection({ ...collection, status: "active" });
      toast({ title: t("manage.published", { defaultValue: "Card published!" }), description: t("manage.publishedDesc", { defaultValue: "Your card is now live and can be shared with guests." }) });
    }
    setPublishing(false);
  };

  const handleCompletePayment = async () => {
    if (!collection) return;
    setInitiatingPayment(true);
    try {
      const { data: paymentData, error: paymentError } = await supabase.functions.invoke(
        "create-card-payment",
        { body: { collection_id: collection.id, template_id: collection.card_template_id } }
      );
      if (paymentError || !paymentData?.url) {
        toast({ variant: "destructive", title: t("common.error"), description: t("manage.paymentFailed", { defaultValue: "Failed to initiate payment. Please try again." }) });
        setInitiatingPayment(false);
        return;
      }
      window.location.href = paymentData.url;
    } catch {
      toast({ variant: "destructive", title: t("common.error"), description: t("manage.paymentFailed", { defaultValue: "Failed to initiate payment. Please try again." }) });
      setInitiatingPayment(false);
    }
  };


  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("cardView.loadingCard")}</div>
      </div>
    );
  }
  if (fetchError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">{t("cardView.somethingWrong")}</h1>
          <p className="text-muted-foreground mb-4">{t("cardView.couldntLoad")}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            {t("cardView.retry")}
          </button>
        </div>
      </div>
    );
  }
  if (!collection) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">{t("cardView.notFound")}</h1>
          <p className="text-muted-foreground">{t("cardView.notFoundDesc")}</p>
        </div>
      </div>
    );
  }

  const isOrganizer = user && user.id === collection.organizer_id;
  if (!isOrganizer) return null;

  const isProductPool = (collection as { pool_type?: string }).pool_type === "product";
  const productCol = collection as unknown as {
    product_name?: string | null;
    product_image?: string | null;
    product_store?: string | null;
    product_url?: string | null;
    product_price?: number | null;
    shipping_name?: string | null;
    shipping_address?: string | null;
    shipping_city?: string | null;
    shipping_phone?: string | null;
  };

  // Joy-style tile definitions. Each maps to existing functionality.
  const tiles: Array<{
    key: string;
    label: string;
    icon: typeof Users;
    iconBg: string;
    iconColor: string;
    badge?: string;
    onClick: () => void;
    destructive?: boolean;
    disabled?: boolean;
  }> = [
    ...(collection.card_template_id ? [{
      key: "design",
      label: isProductPool
        ? t("settings.card", { defaultValue: "Card" })
        : t("manage.tileDesign", { defaultValue: "Design" }),
      icon: Palette,
      iconBg: "bg-rose-100",
      iconColor: "text-rose-600",
      onClick: () => setSettingsSection("design"),
    }] : []),
    {
      key: "signers",
      label: t("manage.tileGuestList", { defaultValue: "Guest list" }),
      icon: Pencil,
      iconBg: "bg-violet-100",
      iconColor: "text-violet-600",
      onClick: () => setShowGroupModal(true),
    },
    {
      key: "message",
      label: t("manage.tileMessage", { defaultValue: "Message" }),
      icon: MessageSquare,
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
      onClick: openMessageModal,
    },
    ...(!isProductPool ? [{
      key: "gifts",
      label: t("manage.tileGifts", { defaultValue: "Gift Preferences" }),
      icon: Gift,
      iconBg: "bg-pink-100",
      iconColor: "text-pink-600",
      onClick: () => setSettingsSection("gifts"),
    }] : []),
    ...(!isProductPool ? [{
      key: "registry",
      label: t("manage.tileRegistry", { defaultValue: "Registry" }),
      icon: BookHeart,
      iconBg: "bg-sky-100",
      iconColor: "text-sky-600",
      badge: recipientWishlists.length > 0 ? String(recipientWishlists.length) : undefined,
      onClick: () => setShowRegistryModal(true),
    }] : []),
    {
      key: "chipin",
      label: t("manage.tileChipIn", { defaultValue: "Chip-in" }),
      icon: CreditCard,
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      badge: Number(collection.total_collected || 0) > 0
        ? `${currency} ${Number(collection.total_collected || 0).toFixed(0)}`
        : undefined,
      onClick: () => setSettingsSection("chipin"),
    },
    ...(!isProductPool ? [{
      key: "rsvp",
      label: t("manage.tileRsvp", { defaultValue: "RSVP" }),
      icon: Check,
      iconBg: "bg-teal-100",
      iconColor: "text-teal-600",
      badge: rsvps.length > 0 ? String(rsvps.filter((r: any) => r.response === "yes").length) : undefined,
      onClick: () => setSettingsSection("rsvp"),
    }] : []),
    ...(isProductPool ? [{
      key: "product",
      label: t("settings.product", { defaultValue: "Product" }),
      icon: ShoppingBag,
      iconBg: "bg-fuchsia-100",
      iconColor: "text-fuchsia-600",
      onClick: () => setShowProductModal(true),
    }] : []),
    {
      key: "details",
      label: t("manage.tileDetails", { defaultValue: "Details" }),
      icon: FileText,
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600",
      onClick: () => setSettingsSection("details"),
    },
    {
      key: "delete",
      label: t("manage.tileDelete", { defaultValue: "Delete" }),
      icon: Trash2,
      iconBg: "bg-red-100",
      iconColor: "text-red-600",
      destructive: true,
      onClick: () => setShowDeleteDialog(true),
    },
  ];

  const eventDateLabel = collection.deadline
    ? format(new Date(collection.deadline), "d MMM yyyy")
    : t("manage.addEventDate", { defaultValue: "Add Event Date" });

  return (
    <div className="min-h-screen bg-background">
      <AppHeader userMenuVariant="dashboard" onBack={() => {
        if (fromDashboard) {
          navigate("/dashboard");
        } else {
          navigate(`/card/${slug}`);
        }
      }} />

      {/* Draft / Payment banner (preserved) */}
      {isDraft && (
        <div className="bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="shrink-0 w-2 h-2 rounded-full bg-amber-500" />
                <div>
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                    {paymentPending
                      ? t("manage.paymentRequired", { defaultValue: "Payment required" })
                      : t("manage.draftStatus", { defaultValue: "This card is a draft" })}
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300/70">
                    {paymentPending
                      ? t("manage.paymentRequiredDesc", { defaultValue: "Complete payment to publish your card and start inviting guests." })
                      : t("manage.draftStatusDesc", { defaultValue: "Publish your card so guests can view and sign it." })}
                  </p>
                </div>
              </div>
              {paymentPending ? (
                <Button
                  onClick={handleCompletePayment}
                  disabled={initiatingPayment}
                  size="sm"
                  className="rounded-full bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                >
                  <CreditCard size={14} className="me-1.5" />
                  {initiatingPayment
                    ? t("manage.redirecting", { defaultValue: "Redirecting…" })
                    : t("manage.completePayment", { defaultValue: "Complete Payment" })}
                </Button>
              ) : (
                <Button
                  onClick={handlePublish}
                  disabled={publishing}
                  size="sm"
                  className="rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 shrink-0"
                >
                  {publishing
                    ? t("manage.publishing", { defaultValue: "Publishing…" })
                    : t("manage.publish", { defaultValue: "Publish Card" })}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Joy-style main */}
      <div className="max-w-6xl mx-auto px-5 sm:px-6 pt-6 pb-24 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(380px,440px)] lg:gap-10">
        <div className="min-w-0">
        {/* Title row */}
        <div className="flex items-start justify-between gap-3 mb-5">
          <h1 className="text-4xl sm:text-5xl font-serif italic text-foreground tracking-tight leading-[1.05] truncate">
            {collection.title}
          </h1>
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-muted hover:bg-muted/80 transition-colors px-2 py-1.5"
            aria-label={t("manage.tileDelete", { defaultValue: "Delete" })}
          >
            {templateUrl ? (
              <img src={templateUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-primary/20" />
            )}
            <Trash2 size={16} className="text-destructive me-1" />
          </button>
        </div>

        {/* Event chips */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => {
              setDateOption("update");
              setNewDeadline(collection.deadline ? new Date(collection.deadline) : undefined);
              setShowDateModal(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background hover:bg-muted/50 px-3 py-2 text-sm text-foreground transition-colors"
          >
            <CalendarDays size={15} className="text-muted-foreground" />
            <span className="font-light">{eventDateLabel}</span>
          </button>
          <button
            onClick={() => setSettingsSection("rsvp")}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background hover:bg-muted/50 px-3 py-2 text-sm text-foreground transition-colors"
          >
            <ExternalLink size={15} className="text-muted-foreground rotate-45" />
            <span className="font-light">
              {(collection as any).rsvp_location_name
                || t("manage.addEventVenue", { defaultValue: "Add Event Venue" })}
            </span>
          </button>
          <button
            onClick={() => setSettingsSection("rsvp")}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background hover:bg-muted/50 px-3 py-2 text-sm text-foreground transition-colors"
          >
            <span className="text-base leading-none">🌍</span>
            <span className="font-light">
              {(collection as any).rsvp_location_address
                || t("manage.addEventLocation", { defaultValue: "Add Event Location" })}
            </span>
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            aria-label={t("manage.share", { defaultValue: "Share" })}
            className="inline-flex items-center justify-center rounded-full border border-border bg-background hover:bg-muted/50 w-10 h-10 transition-colors"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-foreground"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
          </button>
          <button
            onClick={() => navigate(`/card/${collection.share_slug}`, { state: { fromDashboard: true } })}
            aria-label={t("manage.openCard", { defaultValue: "Open card" })}
            className="inline-flex items-center justify-center rounded-full border border-border bg-background hover:bg-muted/50 w-10 h-10 transition-colors"
          >
            <ExternalLink size={15} className="text-foreground" />
          </button>
        </div>

        {/* Tile grid */}
        <div className="grid grid-cols-3 gap-x-4 gap-y-7 sm:gap-y-8">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.key}
                type="button"
                onClick={tile.onClick}
                disabled={tile.disabled}
                className="group flex flex-col items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="relative">
                  <div
                    className={cn(
                      "w-[72px] h-[72px] sm:w-[80px] sm:h-[80px] rounded-full flex items-center justify-center transition-transform duration-200 group-hover:-translate-y-0.5 group-active:scale-95 shadow-sm",
                      tile.iconBg,
                    )}
                  >
                    <Icon
                      size={32}
                      className={cn(tile.iconColor)}
                      strokeWidth={2}
                    />
                  </div>
                  {tile.badge && (
                    <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center ring-2 ring-background">
                      {tile.badge}
                    </span>
                  )}
                </div>
                <span
                  className={cn(
                    "text-[13px] font-medium text-center leading-tight max-w-[88px]",
                    tile.destructive ? "text-destructive" : "text-foreground",
                  )}
                >
                  {tile.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Subtle stats footer */}
        <div className="mt-12 flex items-center justify-center gap-6 text-center">
          <div>
            <p className="text-xl font-serif text-foreground">{messages.length}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{t("cardView.signers", { defaultValue: "Signers" })}</p>
          </div>
          <div className="w-px h-8 bg-border" />
          <div>
            <p className="text-xl font-serif text-foreground">
              {currency} {Number(collection.total_collected || 0).toFixed(0)}
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{t("cardView.collected", { defaultValue: "Collected" })}</p>
          </div>
          {(collection as any).rsvp_enabled && (
            <>
              <div className="w-px h-8 bg-border" />
              <div>
                <p className="text-xl font-serif text-foreground">{rsvps.filter((r: any) => r.response === "yes").length}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{t("cardView.attending", { defaultValue: "Attending" })}</p>
              </div>
            </>
          )}
        </div>


        {/* Gift Preferences section */}
        {!isProductPool && (
          (collection.preferences_wants || collection.preferences_has || collection.preferences_style ||
            ((collection.preferences_registries as string[] | null)?.length ?? 0) > 0) ? (
            <section className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  {t("manage.tileGifts", { defaultValue: "Gift Preferences" })}
                </h2>
                <button
                  onClick={() => setSettingsSection("gifts")}
                  className="text-xs text-primary font-medium hover:underline"
                >
                  {t("common.edit", { defaultValue: "Edit" })}
                </button>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
                {collection.preferences_wants && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
                      {t("manage.giftWants", { defaultValue: "Wants" })}
                    </p>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{collection.preferences_wants}</p>
                  </div>
                )}
                {collection.preferences_has && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
                      {t("manage.giftHas", { defaultValue: "Already has" })}
                    </p>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{collection.preferences_has}</p>
                  </div>
                )}
                {collection.preferences_style && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
                      {t("manage.giftStyle", { defaultValue: "Style" })}
                    </p>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{collection.preferences_style}</p>
                  </div>
                )}
                {((collection.preferences_registries as string[] | null) || []).filter(Boolean).length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
                      {t("manage.giftRegistries", { defaultValue: "Registries" })}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {((collection.preferences_registries as string[] | null) || []).filter(Boolean).map((r, i) => (
                        <a key={i} href={r} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                          <ExternalLink size={12} /> {(() => { try { return new URL(r).hostname.replace("www.", ""); } catch { return r; } })()}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          ) : (
            <section className="mt-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  {t("manage.tileGifts", { defaultValue: "Gift Preferences" })}
                </h2>
              </div>
              <button
                onClick={() => setSettingsSection("gifts")}
                className="w-full rounded-2xl border border-dashed border-border p-6 text-center hover:bg-muted/30 transition-colors"
              >
                <Gift size={20} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {t("manage.addGiftPreferences", { defaultValue: "Add gift preferences to help guests choose" })}
                </p>
              </button>
            </section>
          )
        )}

        {/* Wishlists section */}
        {recipientWishlists.length > 0 && (
          <section className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {t("manage.wishlists", { defaultValue: "Wishlists" })}
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {recipientWishlists.map((w) => (
                <button
                  key={w.id}
                  onClick={() => navigate(`/wishlists/${w.id}`)}
                  className="group rounded-2xl overflow-hidden border border-border bg-card text-start hover:shadow-md transition-all"
                >
                  <div className="aspect-[4/3] bg-muted overflow-hidden">
                    {w.cover_url ? (
                      <img src={w.cover_url} alt={w.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Gift size={28} className="text-muted-foreground/40" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      {w.type === "registry"
                        ? t("manage.registry", { defaultValue: "Registry" })
                        : t("manage.wishlist", { defaultValue: "Wishlist" })}
                    </p>
                    <p className="text-sm font-medium text-foreground truncate mt-0.5">{w.title}</p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Signed messages section */}
        <section className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              {t("manage.whoSigned", { defaultValue: "Who signed" })}
            </h2>
            {messages.length > 0 && (
              <button
                onClick={() => setShowGroupModal(true)}
                className="text-xs text-primary font-medium hover:underline"
              >
                {t("common.viewAll", { defaultValue: "View all" })}
              </button>
            )}
          </div>
          {messages.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t("manage.noSignersYet", { defaultValue: "No one has signed yet." })}
              </p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {messages.slice(0, 6).map((m) => {
                const contrib = contributions.find(
                  (c) => c.contributor_email === m.contributor_email && c.status === "completed"
                );
                return (
                  <button
                    key={m.id}
                    onClick={() => setSelectedGroupMemberId(m.id)}
                    className="text-start rounded-2xl border border-border bg-card p-4 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="relative shrink-0">
                        <BubbleMedia message={m} size="sm" />
                        {contrib && (
                          <div className="absolute -bottom-0.5 -right-0.5 min-w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center ring-2 ring-card px-1">
                            <span className="text-[8px] font-bold leading-none">{currency} {Number(contrib.amount).toFixed(0)}</span>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{m.contributor_name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </p>
                      </div>
                    </div>
                    {m.message_text && (
                      <p className="text-sm text-foreground/80 line-clamp-3 italic">
                        "{m.message_text}"
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <AnimatePresence>
            {selectedGroupMemberId && (() => {
              const msg = messages.find((m) => m.id === selectedGroupMemberId);
              if (!msg) return null;
              const contrib = contributions.find(
                (c) => c.contributor_email === msg.contributor_email && c.status === "completed"
              );
              return (
                <SignerDetail
                  message={msg}
                  contribution={contrib}
                  onClose={() => setSelectedGroupMemberId(null)}
                />
              );
            })()}
          </AnimatePresence>
        </section>

        {/* Guest list (RSVP) section */}
        {(collection as any).rsvp_enabled && (
          <section className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {t("manage.guestList", { defaultValue: "Guest list" })}
              </h2>
              <button
                onClick={() => setSettingsSection("rsvp")}
                className="text-xs text-primary font-medium hover:underline"
              >
                {t("common.manage", { defaultValue: "Manage" })}
              </button>
            </div>
            {rsvps.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {t("manage.noRsvpsYet", { defaultValue: "No RSVPs yet." })}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {rsvps.map((r: any) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5"
                  >
                    <div className={cn("w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium shrink-0", colorForName(r.guest_name))}>
                      {getInitials(r.guest_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{r.guest_name}</p>
                      {r.guest_email && (
                        <p className="text-[11px] text-muted-foreground truncate">{r.guest_email}</p>
                      )}
                    </div>
                    <span
                      className={cn(
                        "shrink-0 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full",
                        r.response === "yes"
                          ? "bg-emerald-100 text-emerald-700"
                          : r.response === "no"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {r.response === "yes"
                        ? t("rsvp.going", { defaultValue: "Going" })
                        : r.response === "no"
                        ? t("rsvp.notGoing", { defaultValue: "Not going" })
                        : r.response}
                      {r.plus_one ? " +1" : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
        </div>

        {/* Right column — card preview (no envelope) */}
        <aside className="hidden lg:block lg:sticky lg:top-24 lg:self-start">
          <div
            className="mx-auto w-full max-w-[360px] overflow-hidden rounded-md bg-white shadow-2xl"
            style={{ aspectRatio: "360/508" }}
          >
            {templateUrl ? (
              <img src={templateUrl} alt={collection.title} className="w-full h-full object-cover" />
            ) : (
              <div className="flex items-center justify-center h-full w-full p-8">
                <h3 className="text-2xl font-bold text-center text-foreground">{collection.title}</h3>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Contribute Wizard */}
      <AnimatePresence>
        {wizardMode && (
          <ContributeWizard
            collection={collection}
            templateUrl={templateUrl}
            messages={messages}
            chipIn={wizardMode === "chipIn"}
            onClose={() => setWizardMode(null)}
            onSigned={() => setWizardMode(null)}
          />
        )}
      </AnimatePresence>

      {/* Change date modal */}
      <AnimatePresence>
        {showDateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setShowDateModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl relative max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10">
                <h2 className="text-base font-bold text-foreground font-serif">{t("cardView.changeCloseDate")}</h2>
                <button
                  onClick={() => setShowDateModal(false)}
                  className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <button
                  onClick={() => setDateOption("update")}
                  className={cn(
                    "w-full text-start rounded-xl border p-4 transition-all",
                    dateOption === "update"
                      ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                      : "border-border hover:border-primary/30"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                      dateOption === "update" ? "border-primary" : "border-muted-foreground/30"
                    )}>
                      {dateOption === "update" && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground">{t("cardView.updateCloseDate")}</span>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.pickNewDeadline")}</p>
                    </div>
                  </div>
                  {dateOption === "update" && (
                    <div className="mt-3 ms-8">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="w-full text-start text-sm bg-muted/80 border border-border rounded-lg px-4 py-2.5 text-foreground hover:bg-muted transition-colors flex items-center justify-between">
                            <span>{newDeadline ? format(newDeadline, "dd MMM yyyy") : t("cardView.pickDate")}</span>
                            <CalendarDays size={14} className="text-muted-foreground" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={newDeadline}
                            onSelect={setNewDeadline}
                            disabled={(date) => date < new Date()}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </button>

                <button
                  onClick={() => setDateOption("close")}
                  className={cn(
                    "w-full text-start rounded-xl border p-4 transition-all",
                    dateOption === "close"
                      ? "border-destructive/50 bg-destructive/5 ring-1 ring-destructive/20"
                      : "border-border hover:border-destructive/30"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                      dateOption === "close" ? "border-destructive" : "border-muted-foreground/30"
                    )}>
                      {dateOption === "close" && <div className="w-2.5 h-2.5 rounded-full bg-destructive" />}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground">{t("cardView.closeNow")}</span>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.closeNowDesc")}</p>
                    </div>
                  </div>
                </button>

                <Button
                  onClick={handleSaveDate}
                  disabled={savingDate || (dateOption === "update" && !newDeadline)}
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  size="lg"
                >
                  {savingDate ? t("cardView.saving") : t("common.save")}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Your Group modal */}
      <AnimatePresence>
        {showGroupModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setShowGroupModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[85vh] shadow-2xl flex flex-col relative overflow-hidden"
            >
              <div className="flex items-center justify-center py-5 relative shrink-0 border-b border-border">
                <h2 className="text-lg font-bold text-foreground font-serif">{t("cardView.yourGroup")}</h2>
                <button
                  onClick={() => setShowGroupModal(false)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground absolute top-4 end-4"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="flex items-center justify-around px-5 py-4">
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">{new Set(messages.map(m => m.contributor_email || m.contributor_name)).size}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.signed")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">{contributions.filter(c => c.status === "completed").length}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.contributed")}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">
                      {currency} {contributions.filter(c => c.status === "completed").reduce((sum, c) => sum + Number(c.amount), 0).toFixed(0)}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{t("cardView.total")}</p>
                  </div>
                </div>

                <div className="px-5 pb-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0 border border-border rounded-lg px-2.5 py-1.5 bg-muted/30">
                      <Search size={14} className="text-muted-foreground shrink-0" />
                      <input
                        value={groupSearch}
                        onChange={(e) => setGroupSearch(e.target.value)}
                        placeholder={t("cardView.searchByName")}
                        className="bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground w-full"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="rounded-full text-xs" onClick={() => {
                      toast({ title: t("cardView.comingSoon"), description: t("cardView.groupMessagingSoon") });
                    }}>
                      {t("cardView.sendGroupMessage")}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => { setShowGroupModal(false); setShowInviteModal(true); }}>
                      {t("cardView.invitePeople")}
                    </Button>
                  </div>
                </div>

                <div className="px-5 pb-5">
                  {(() => {
                    const contributionMap = new Map<string, Tables<"contributions">>();
                    contributions.forEach(c => {
                      const existing = contributionMap.get(c.contributor_name);
                      if (!existing || c.status === "completed") {
                        contributionMap.set(c.contributor_name, c);
                      }
                    });

                    type MemberItem = {
                      id: string;
                      name: string;
                      email: string | null;
                      signed: boolean;
                      signedDate: string | null;
                      contributed: boolean;
                      amount: number;
                      contributedDate: string | null;
                      messagePreview: string | null;
                    };

                    const membersMap = new Map<string, MemberItem>();

                    messages.forEach(m => {
                      const contrib = contributionMap.get(m.contributor_name);
                      membersMap.set(m.contributor_name, {
                        id: m.id,
                        name: m.contributor_name,
                        email: m.contributor_email,
                        signed: true,
                        signedDate: m.created_at,
                        contributed: contrib?.status === "completed",
                        amount: contrib?.status === "completed" ? Number(contrib.amount) : 0,
                        contributedDate: contrib?.created_at || null,
                        messagePreview: m.message_text?.slice(0, 50) || null,
                      });
                    });

                    contributions.forEach(c => {
                      if (!membersMap.has(c.contributor_name)) {
                        membersMap.set(c.contributor_name, {
                          id: c.id,
                          name: c.contributor_name,
                          email: c.contributor_email,
                          signed: false,
                          signedDate: null,
                          contributed: c.status === "completed",
                          amount: c.status === "completed" ? Number(c.amount) : 0,
                          contributedDate: c.created_at,
                          messagePreview: null,
                        });
                      }
                    });

                    let members = Array.from(membersMap.values());
                    if (groupSearch) {
                      const q = groupSearch.toLowerCase();
                      members = members.filter(m =>
                        m.name.toLowerCase().includes(q) ||
                        (m.email && m.email.toLowerCase().includes(q))
                      );
                    }

                    if (members.length === 0) {
                      return (
                        <div className="text-center py-12 text-muted-foreground text-sm">
                          {messages.length === 0 && contributions.length === 0
                            ? t("cardView.noMembersYet")
                            : t("cardView.noResults")}
                        </div>
                      );
                    }

                    const selectedMemberMessage = selectedGroupMemberId
                      ? messages.find(msg => msg.id === selectedGroupMemberId)
                      : null;
                    const selectedMemberContribution = selectedMemberMessage
                      ? contributions.find(c => c.contributor_email === selectedMemberMessage.contributor_email && c.status === "completed")
                      : undefined;

                    return (
                      <>
                        <div className="divide-y divide-border">
                          {members.map((m) => {
                            const message = messages.find(msg => msg.id === m.id);
                            return (
                              <button
                                key={m.id}
                                className="flex items-center gap-3 py-3 w-full text-start hover:bg-muted/50 transition-colors cursor-pointer"
                                onClick={() => {
                                  if (message) setSelectedGroupMemberId(message.id);
                                }}
                              >
                                <div className="relative shrink-0">
                                  {message ? (
                                    <BubbleMedia message={message} size="sm" />
                                  ) : (
                                    <div className={`w-14 h-14 rounded-full ring-2 ring-background flex items-center justify-center font-medium text-xs ${colorForName(m.name)}`}>
                                      {getInitials(m.name)}
                                    </div>
                                  )}
                                  {m.contributed && (
                                    <div className="absolute -bottom-0.5 -right-0.5 min-w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center ring-2 ring-background px-1">
                                      <span className="text-[8px] font-bold leading-none">{currency} {m.amount.toFixed(0)}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-foreground truncate">{m.name}</p>
                                  {m.messagePreview && (
                                    <p className="text-xs text-muted-foreground/70 truncate italic mt-0.5">
                                      "{m.messagePreview}{m.messagePreview.length >= 50 ? "..." : ""}"
                                    </p>
                                  )}
                                </div>
                                <span className="text-[10px] text-muted-foreground shrink-0">
                                  {m.signedDate
                                    ? new Date(m.signedDate).toLocaleDateString()
                                    : m.contributedDate
                                      ? new Date(m.contributedDate).toLocaleDateString()
                                      : ""}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        <AnimatePresence>
                          {selectedMemberMessage && (
                            <SignerDetail
                              message={selectedMemberMessage}
                              contribution={selectedMemberContribution}
                              onClose={() => setSelectedGroupMemberId(null)}
                            />
                          )}
                        </AnimatePresence>
                      </>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite modal */}
      <AnimatePresence>
        {showInviteModal && collection && (
          <InviteModal
            collectionId={collection.id}
            collectionTitle={collection.title}
            shareSlug={collection.share_slug || undefined}
            organizerName={user?.user_metadata?.display_name}
            onClose={() => setShowInviteModal(false)}
          />
        )}
      </AnimatePresence>
      {/* Settings modal */}
      <AnimatePresence>
        {settingsSection !== null && collection && (
          <SettingsModal
            collection={collection}
            onClose={() => setSettingsSection(null)}
            onUpdate={(updates) => setCollection({ ...collection, ...updates })}
            initialSection={settingsSection}
          />
        )}
      </AnimatePresence>
      {/* Registry modal */}
      <OrganizerRegistryModal
        open={showRegistryModal}
        onOpenChange={setShowRegistryModal}
        recipientWishlists={recipientWishlists}
        recipientName={collection?.recipient_name || undefined}
      />
      {/* Product details modal — product pools only */}
      <AnimatePresence>
        {showProductModal && isProductPool && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setShowProductModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl relative"
            >
              <div className="flex items-center py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10 px-4">
                <div className="w-8" />
                <h2 className="text-lg font-bold text-foreground font-serif flex-1 text-center">
                  {t("manage.productDetailsTitle", { defaultValue: "Product details" })}
                </h2>
                <button
                  onClick={() => setShowProductModal(false)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="p-5 space-y-5">
                <div className="rounded-xl border border-border overflow-hidden bg-card">
                  <div className="aspect-[4/3] bg-gradient-to-b from-muted/40 to-muted/70 flex items-center justify-center p-6">
                    {productCol.product_image ? (
                      <img
                        src={productCol.product_image}
                        alt={productCol.product_name || ""}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <ShoppingBag size={56} className="text-muted-foreground/50" />
                    )}
                  </div>
                  <div className="px-5 py-4 space-y-1.5 border-t border-border">
                    {productCol.product_store && (
                      <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                        {productCol.product_store}
                      </p>
                    )}
                    {productCol.product_name && (
                      <h3 className="text-base font-bold text-foreground leading-tight font-serif">
                        {productCol.product_name}
                      </h3>
                    )}
                    <div className="flex items-center justify-between pt-1.5">
                      <p className="text-lg font-bold text-foreground">
                        AED {Number(productCol.product_price || 0).toLocaleString()}
                      </p>
                      {productCol.product_url && (
                        <a
                          href={productCol.product_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          {t("cardView.viewProduct", { defaultValue: "View product" })}
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {(productCol.shipping_name || productCol.shipping_address || productCol.shipping_city || productCol.shipping_phone) && (
                  <div className="rounded-xl border border-border bg-card">
                    <div className="px-5 py-3 border-b border-border">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.15em]">
                        {t("manage.shippingTo", { defaultValue: "Shipping to" })}
                      </p>
                    </div>
                    <dl className="px-5 py-3 text-sm text-foreground space-y-2">
                      {productCol.shipping_name && (
                        <div className="flex items-start justify-between gap-3">
                          <dt className="text-xs text-muted-foreground">{t("manage.recipient", { defaultValue: "Recipient" })}</dt>
                          <dd className="text-end">{productCol.shipping_name}</dd>
                        </div>
                      )}
                      {productCol.shipping_address && (
                        <div className="flex items-start justify-between gap-3">
                          <dt className="text-xs text-muted-foreground">{t("manage.address", { defaultValue: "Address" })}</dt>
                          <dd className="text-end">{productCol.shipping_address}</dd>
                        </div>
                      )}
                      {productCol.shipping_city && (
                        <div className="flex items-start justify-between gap-3">
                          <dt className="text-xs text-muted-foreground">{t("manage.city", { defaultValue: "City" })}</dt>
                          <dd className="text-end">{productCol.shipping_city}</dd>
                        </div>
                      )}
                      {productCol.shipping_phone && (
                        <div className="flex items-start justify-between gap-3">
                          <dt className="text-xs text-muted-foreground">{t("manage.phone", { defaultValue: "Phone" })}</dt>
                          <dd className="text-end">{productCol.shipping_phone}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-background border-t border-border px-5 py-4">
                <Button
                  variant="outline"
                  onClick={() => setShowProductModal(false)}
                  className="w-full rounded-full"
                  size="lg"
                >
                  {t("common.close", { defaultValue: "Close" })}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Quick customizer opened from the envelope corner button */}
      {customizerOpen && (
        <Suspense fallback={null}>
          <FabricEditor
            mode="user"
            initialJson={customizerJson}
            onClose={() => setCustomizerOpen(false)}
            onUserSave={handleCustomizerSave}
          />
        </Suspense>
      )}
      {/* Gift Guidance Wizard */}
      <AnimatePresence>
        {showGiftWizard && collection && (
          <GiftGuidanceWizard
            collection={collection}
            onClose={() => setShowGiftWizard(false)}
            onUpdate={(updates) => setCollection({ ...collection, ...updates })}
          />
        )}
      </AnimatePresence>
      {/* Message to group bottom sheet — matches SettingsModal animation */}
      <AnimatePresence>
        {showMessageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => !savingMessage && setShowMessageModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl relative"
            >
              {/* Header */}
              <div className="flex items-center py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10 px-4">
                <div className="w-8" />
                <h2 className="text-lg font-bold text-foreground font-serif flex-1 text-center">
                  {t("settings.messageToGroupTitle", { defaultValue: "Message to group" })}
                </h2>
                <button
                  onClick={() => setShowMessageModal(false)}
                  disabled={savingMessage}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t("settings.messageToGroupDesc", {
                    defaultValue: "Write a personal message your group will see when they receive the invitation.",
                  })}
                </p>
                <div className="space-y-1.5">
                  <div className="flex justify-end">
                    <span className="text-xs text-muted-foreground tabular-nums">{messageDraft.length}/500</span>
                  </div>
                  <Textarea
                    value={messageDraft}
                    onChange={(e) => setMessageDraft(e.target.value.slice(0, 500))}
                    placeholder={t("settings.messageToGroupPlaceholder", {
                      defaultValue: `Let's surprise ${collection?.recipient_name || "them"} with something special!`,
                      name: collection?.recipient_name || "them",
                    })}
                    className="min-h-[160px] resize-none"
                    rows={6}
                  />
                </div>
              </div>

              {/* Sticky footer — Save + Cancel stacked with vertical spacing */}
              <div className="sticky bottom-0 bg-background border-t border-border px-5 py-4 flex flex-col gap-4">
                <Button
                  onClick={handleSaveMessage}
                  disabled={savingMessage}
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  size="lg"
                >
                  {savingMessage
                    ? t("common.saving", { defaultValue: "Saving..." })
                    : t("common.save", { defaultValue: "Save" })}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowMessageModal(false)}
                  disabled={savingMessage}
                  className="w-full rounded-full"
                  size="lg"
                >
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("settings.deleteThisCard", { defaultValue: "Delete this card" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.deleteConfirm", { defaultValue: "Are you sure? This cannot be undone." })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting
                ? t("settings.deleting", { defaultValue: "Deleting..." })
                : t("settings.confirmDelete", { defaultValue: "Yes, delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


export default CardManage;
